const MAX_ACTIVE_ROWS = 10;
const ACTIVE_STATES = new Set(['working', 'blocked', 'waiting', 'error']);

export type AgentStatusArguments = { agent: string | null };

/** Parse the model payload without normalising or fuzzily matching names. */
export function parseAgentStatusArguments(rawArguments: string): AgentStatusArguments | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawArguments);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;
  if (Object.keys(record).some((key) => key !== 'agent')) return null;
  if (record.agent !== null && typeof record.agent !== 'string') return null;
  const agent = typeof record.agent === 'string' ? record.agent.trim() : null;
  return { agent: agent || null };
}

function compactRow(row: Record<string, unknown>) {
  const summary = typeof row.summary === 'string' ? row.summary
    : (typeof row.now === 'string' ? row.now : undefined);
  return {
    agent: String(row.name || ''),
    state: String(row.effective_state || row.declared_state || 'unknown'),
    ...(summary ? { summary } : {}),
    ...(typeof row.next === 'string' && row.next ? { next: row.next } : {}),
    ...(typeof row.task_id === 'string' && row.task_id ? { task_id: row.task_id } : {}),
  };
}

/** Bound the all-agent proxy result before it returns to the model. The proxy
 * remains the authority and performs owner filtering; this function only keeps
 * the spoken follow-up short and cheap. Named-agent results pass through. */
export function compactAgentStatusToolResult(result: unknown): unknown {
  if (!result || typeof result !== 'object') return result;
  const outer = result as Record<string, unknown>;
  const toolResult = outer.result;
  if (!toolResult || typeof toolResult !== 'object') return result;
  const inner = toolResult as Record<string, unknown>;
  const data = inner.data;
  const isOverview = inner.scope === 'all' || (
    inner.scope === undefined && inner.agent === '(alle)'
  );
  // The overview can contain every visible agent and must never pass through
  // unbounded. `scope` is the stable contract marker; the German label is only
  // a deployment-order bridge for AiApi versions that predate that marker.
  // AiApi briefly renamed `data` to `board` (4dcc923); silently accepting that
  // shape would have paid for the entire list in model context.
  if (!data || typeof data !== 'object') {
    return isOverview ? {
      call_id: outer.call_id,
      tool: outer.tool,
      ok: false,
      error: 'invalid_agent_status_overview',
      retryable: false,
    } : result;
  }
  const agents = (data as Record<string, unknown>).agents;
  if (!Array.isArray(agents)) {
    return isOverview ? {
      call_id: outer.call_id,
      tool: outer.tool,
      ok: false,
      error: 'invalid_agent_status_overview',
      retryable: false,
    } : result;
  }

  const rows = agents.filter((row): row is Record<string, unknown> => (
    Boolean(row) && typeof row === 'object' && !Array.isArray(row)
  ));
  const activeRows = rows.filter((row) => ACTIVE_STATES.has(String(
    row.effective_state || row.declared_state || '',
  )));
  return {
    ...outer,
    result: {
      ...inner,
      data: {
        active_agents: activeRows.slice(0, MAX_ACTIVE_ROWS).map(compactRow),
        active_agents_omitted: Math.max(0, activeRows.length - MAX_ACTIVE_ROWS),
        inactive_or_done_agents: Math.max(0, rows.length - activeRows.length),
      },
    },
  };
}
